/**
 * Paragraph Hero Grid — Brand Pattern Animation
 *
 * Generates a dense grid of random letters in bordered cells,
 * matching the Paragraph brand pattern from the wine labels.
 * Letters randomly cycle, with embossed/debossed styling and
 * randomized positioning within cells.
 */

class ParagraphHeroGrid extends HTMLElement {
  connectedCallback() {
    this.banner =
      this.closest('.pw-hero-homepage') ||
      this.closest('.paragraph-hero') ||
      this.closest('.banner');
    if (!this.banner) return;

    this.chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    this.cells = [];
    this.rafId = null;
    this.isVisible = false;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.loadFont();
    this.buildGrid();
    this.buildKnockout();

    if (!this.reducedMotion.matches && !(typeof Shopify !== 'undefined' && Shopify.designMode)) {
      this.observer = new IntersectionObserver(
        function (entries) {
          this.isVisible = entries[0].isIntersecting;
          if (this.isVisible && !this.rafId) this.startAnimation();
          if (!this.isVisible && this.rafId) this.stopAnimation();
        }.bind(this),
        { threshold: 0.1 }
      );
      this.observer.observe(this);
    }

    this._onResize = this.debounce(this.positionKnockout.bind(this), 150);
    window.addEventListener('resize', this._onResize);
  }

  disconnectedCallback() {
    this.stopAnimation();
    if (this.observer) this.observer.disconnect();
    if (this._onResize) window.removeEventListener('resize', this._onResize);
  }

  loadFont() {
    // Zalando Sans Expanded is now self-hosted and declared via @font-face
    // in paragraph-brand.css. No runtime injection needed; this is a stub
    // kept to preserve the call site in connectedCallback().
  }

  buildGrid() {
    var container = document.createElement('div');
    container.className = 'paragraph-grid__cells';

    var rect = this.banner.getBoundingClientRect();
    var cellSize = 48;
    var cols = Math.ceil(rect.width / cellSize);
    var rows = Math.ceil(rect.height / cellSize);

    container.style.setProperty('--grid-cols', cols);

    var fragment = document.createDocumentFragment();
    var total = cols * rows;

    for (var i = 0; i < total; i++) {
      var cell = document.createElement('span');

      // Randomly assign embossed or debossed style
      var style = Math.random();
      if (style < 0.45) {
        cell.className = 'paragraph-grid__cell paragraph-grid__cell--embossed';
      } else if (style < 0.9) {
        cell.className = 'paragraph-grid__cell paragraph-grid__cell--debossed';
      } else {
        cell.className = 'paragraph-grid__cell';
      }

      cell.textContent = this.randomChar();

      fragment.appendChild(cell);
      this.cells.push(cell);
    }

    container.appendChild(fragment);
    this.appendChild(container);
    this.gridContainer = container;
  }

  buildKnockout() {
    this.knockout = document.createElement('div');
    this.knockout.className = 'paragraph-grid__knockout';
    this.appendChild(this.knockout);
    this.positionKnockout();
  }

  positionKnockout() {
    var box =
      this.banner.querySelector('.pw-hero-homepage__content') ||
      this.banner.querySelector('.paragraph-hero__content') ||
      this.banner.querySelector('.banner__box');
    if (!box || !this.knockout) return;

    var bannerRect = this.banner.getBoundingClientRect();
    var boxRect = box.getBoundingClientRect();

    var pad = 40;
    this.knockout.style.left = (boxRect.left - bannerRect.left - pad) + 'px';
    this.knockout.style.top = (boxRect.top - bannerRect.top - pad) + 'px';
    this.knockout.style.width = (boxRect.width + pad * 2) + 'px';
    this.knockout.style.height = (boxRect.height + pad * 2) + 'px';
  }

  randomChar() {
    return this.chars[(Math.random() * 26) | 0];
  }

  startAnimation() {
    var self = this;
    var cells = this.cells;
    var len = cells.length;
    var changes = Math.max(2, Math.ceil(len * 0.004));

    function tick() {
      for (var i = 0; i < changes; i++) {
        var idx = (Math.random() * len) | 0;
        cells[idx].textContent = self.randomChar();
      }
      self.rafId = requestAnimationFrame(tick);
    }

    this.rafId = requestAnimationFrame(tick);
  }

  stopAnimation() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  debounce(fn, ms) {
    var timer;
    return function () {
      clearTimeout(timer);
      timer = setTimeout(fn, ms);
    };
  }
}

customElements.define('paragraph-hero-grid', ParagraphHeroGrid);
